// import React from "react";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseInput from "../../../shared/components/BaseInput";
import BaseButton from "components/common/BaseButton";
import { useIntl } from "react-intl";
import BaseSelect from "pages/admin-app/shared/components/BaseSelect";
import {
  useNAMStates,
  useStateByAbbr,
} from "pages/admin-app/states/hooks/useNAMStates";
import BaseAsyncPaginate from "pages/admin-app/shared/components/BaseAsyncPaginate";
import { useIndustrySpecialists } from "pages/admin-app/industry-specialist/hooks/useIndustrySpecialists";
import { EndUserFormData, getEndUserFormSchema } from "./edit-end-user.schema";
import { useTerritoryManagers } from "pages/admin-app/territory-manager/hooks/useTerritoryManagers";
import { loadIndustrySpecialists } from "pages/admin-app/industry-specialist/services/loadIndustrySpecialists";
import { loadTerritoryManagers } from "pages/admin-app/territory-manager/services/loadTerritoryManagers";
import { loadDistributors } from "pages/admin-app/distributor/services/loadDistributors";
import { useDistributors } from "pages/admin-app/distributor/hooks/useDistributors";
import { useCountries } from "pages/admin-app/country/hooks/useCountries";
import { loadCountries } from "pages/admin-app/country/services/loadCountries";
import { loadStates } from "pages/admin-app/states/services/loadStates";

interface Props {
  endUser: EndUserFormData & {
    industrySpecialist?: { id: string | number; name: string };
    territoryManager?: { id: string | number; name: string };
    distributor?: { id: string | number; name: string; contactName?: string };
    country?: { id: number; name: string; abbr: string };
  };
  onSubmit: (data: EndUserFormData) => void;
  isSubmitting: boolean;
}

const EditEndUserForm = ({ endUser, onSubmit, isSubmitting }: Props) => {
  const intl = useIntl();
  const endUserFormSchema = getEndUserFormSchema(intl);

  const methods = useForm({
    resolver: zodResolver(endUserFormSchema),
    defaultValues: endUser,
  });

  const { handleSubmit, control } = methods;
  const selectedCountry = useWatch({ control, name: "countryId" });

  const {
    data: industrySpecialistsData,
    isLoading: isLoadingSpecialists,
    isError: isErrorSpecialists,
    error: specialistsError,
    refetch: refetchIndustrySpecialists,
    isFetching: isFetchingSpecialists,
  } = useIndustrySpecialists({ currentPage: 1 });

  const initialIndustrySpecialistDropdownValue =
    endUser.isId && endUser.industrySpecialist?.name
      ? {
        value: endUser.isId,
        label: endUser.industrySpecialist.name,
      }
      : null;

  const {
    data: territoryManagersData,
    isLoading: isLoadingTerritoryManagers,
    isError: isErrorManagers,
    error: managersError,
    refetch: refetchTerritoryManagers,
    isFetching: isFetchingManagers,
  } = useTerritoryManagers({ currentPage: 1 });

  const initialTerritoryManagerDropdownValue =
    endUser.tmId && endUser.territoryManager?.name
      ? { value: endUser.tmId, label: endUser.territoryManager.name }
      : null;

  const {
    data: distributorsData,
    isLoading: isLoadingDistributors,
    isError: isErrorDistributors,
    error: distributorsError,
    refetch: refetchDistributors,
    isFetching: isFetchingDistributors,
  } = useDistributors({ currentPage: 1 });

  const initialDistributorDropdownValue =
    endUser.distributorId && endUser.distributor?.name
      ? {
        value: endUser.distributorId,
        label: `${endUser.distributor.contactName} @ ${endUser.distributor.name}`,
      }
      : null;

  const {
    data: states,
    isLoading: isLoadingStates,
    isError: isErrorStates,
    error: statesError,
    refetch: refetchStates,
    isFetching: isFetchingStates,
  } = useNAMStates({
    countryId: selectedCountry,
    currentPage: 1,
  });

  const { data: selectedState } = useStateByAbbr({
    countryId: selectedCountry,
    abbr: endUser?.state,
  });

  const initialStatesDropdownValue = selectedState
    ? {
      value: selectedState.abbr,
      label: `${selectedState.name} (${selectedState.abbr})`,
    }
    : null;

  const {
    data: countries,
    isLoading: isLoadingCountries,
    isError: isErrorCountries,
    error: countriesError,
    refetch: refetchCountries,
    isFetching: isFetchingCountries,
  } = useCountries({ currentPage: 1 });

  const initialCountryDropdownValue =
    endUser.country?.id && endUser.country?.name
      ? {
        value: endUser.country.id,
        label: endUser.country.name,
      }
      : null;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="inputs-container">
        <BaseInput
          name="name"
          label={intl.formatMessage({ id: "usAdmin.name" })}
          type="text"
          containerClassName="usadmin-input-name"
        />
        <BaseInput
          name="contactName"
          label={intl.formatMessage({ id: "usAdmin.contactName.endUser" })}
          type="text"
          containerClassName="usadmin-input-contactName"
        />
        <BaseInput
          name="address"
          label={intl.formatMessage({ id: "usAdmin.address" })}
          type="text"
          containerClassName="second-column"
        />
        <div className="third-row">
          <BaseInput
            name="email"
            label={intl.formatMessage({ id: "usAdmin.email.endUser" })}
            type="text"
          />
          <BaseInput
            name="phone"
            label={intl.formatMessage({ id: "usAdmin.phone" })}
            type="text"
          />
          <BaseInput
            name="zip"
            label={intl.formatMessage({ id: "usAdmin.zip" })}
            type="text"
          />
        </div>
        <div className="third-row">
          <BaseInput
            name="city"
            label={intl.formatMessage({ id: "usAdmin.city" })}
            type="text"
          />
          <BaseAsyncPaginate
            name="state"
            label={intl.formatMessage({ id: "usAdmin.state" })}
            loadOptions={loadStates(selectedCountry)}
            initialResolvedOption={initialStatesDropdownValue}
            data={states?.data || []}
            isLoading={isLoadingStates || isFetchingStates}
            isError={isErrorStates}
            error={statesError}
            refetch={refetchStates}
            optionMapper={(state) => ({
              value: state.abbr,
              label: `${state.name} (${state.abbr})`,
            })}
            placeholder={intl.formatMessage({ id: "usAdmin.selectState" })}
            additional={{ page: 1 }}
            selectedCountry={selectedCountry}
            isDisabled={!selectedCountry}
          />
          <BaseAsyncPaginate
            name="countryId"
            label={intl.formatMessage({ id: "country" })}
            loadOptions={loadCountries}
            data={countries?.data || []}
            initialResolvedOption={initialCountryDropdownValue}
            optionMapper={(c) => ({
              value: c.id,
              label: c.name,
            })}
            additional={{ page: 1 }}
            isLoading={isLoadingCountries || isFetchingCountries}
            isError={isErrorCountries}
            error={countriesError}
            refetch={refetchCountries}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectCountry",
            })}
          />
        </div>
        <div className="forth-row">
          <BaseAsyncPaginate
            name="isId"
            label={intl.formatMessage({ id: "usAdmin.industrySpecialist" })}
            loadOptions={loadIndustrySpecialists}
            data={industrySpecialistsData?.data || []}
            initialResolvedOption={initialIndustrySpecialistDropdownValue}
            optionMapper={(spec) => ({
              value: spec.id,
              label: spec.name,
            })}
            additional={{ page: 1 }}
            isLoading={isLoadingSpecialists || isFetchingSpecialists}
            isError={isErrorSpecialists}
            error={specialistsError}
            refetch={refetchIndustrySpecialists}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectIndustrySpecialist",
            })}
          />
          <BaseAsyncPaginate
            name="tmId"
            label={intl.formatMessage({ id: "usAdmin.territoryManager" })}
            loadOptions={loadTerritoryManagers}
            data={territoryManagersData?.data || []}
            initialResolvedOption={initialTerritoryManagerDropdownValue}
            optionMapper={(spec) => ({
              value: spec.id,
              label: spec.name,
            })}
            additional={{ page: 1 }}
            isLoading={isLoadingTerritoryManagers || isFetchingManagers}
            isError={isErrorManagers}
            error={managersError}
            refetch={refetchTerritoryManagers}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectTerritoryManager",
            })}
          />
          <BaseAsyncPaginate
            name="distributorId"
            label={intl.formatMessage({ id: "usAdmin.distributor" })}
            loadOptions={loadDistributors}
            data={distributorsData?.data || []}
            initialResolvedOption={initialDistributorDropdownValue}
            optionMapper={(spec) => ({
              value: spec.id,
              label: `${spec.contactName} @ ${spec.name}`,
            })}
            additional={{ page: 1 }}
            isLoading={isLoadingDistributors || isFetchingDistributors}
            isError={isErrorDistributors}
            error={distributorsError}
            refetch={refetchDistributors}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectDistributor",
            })}
          />
          <BaseSelect
            name="automatedRg"
            label={intl.formatMessage({ id: "usAdmin.automatedRg" })}
            data={endUser.automatedRg ? [true, false] : [false, true]}
            optionMapper={(option) => ({
              value: option,
              label: option
                ? intl.formatMessage({ id: "customComponents.yes" })
                : intl.formatMessage({ id: "customComponents.no" }),
            })}
          />
        </div>
        <BaseButton
          text={intl.formatMessage({ id: "usAdmin.update" })}
          type="submit"
          disabled={isSubmitting}
          className="first-column usadmin-form-submit-button"
          handleSubmit={() => { }}
        />
      </form>
    </FormProvider>
  );
};

export default EditEndUserForm;
